import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Menu, Phone, X } from "lucide-react";

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  const navItems = [
    { name: "Domov", path: "/" },
    { name: "O nas", path: "/o-nas" },
    { name: "Storitve", path: "/storitve" },
    { name: "Cenik", path: "/cenik" },
    { name: "Galerija", path: "/galerija" },
    { name: "Nasveti", path: "/nasveti" },
    { name: "Kontakt", path: "/kontakt" }
  ];

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 w-full transition-all duration-300 ${
        scrolled ? "bg-white/80 backdrop-blur-md shadow-sm" : "bg-transparent"
      }`}
    >
      <div className="container mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center">
            <span className="text-xl md:text-2xl font-bold text-gray-900">
              Dr. Danijel <span className="text-medical-600">Novak</span>
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-1 lg:space-x-2">
            {navItems.map((item) => (
              <Link
                key={item.name}
                to={item.path}
                className={`px-3 py-2 rounded-lg text-sm lg:text-base font-medium transition-colors duration-200 ${
                  isActive(item.path)
                    ? "text-medical-600 bg-medical-50"
                    : "text-gray-700 hover:text-medical-600 hover:bg-gray-50"
                }`}
              >
                {item.name}
              </Link>
            ))}
          </nav>

          <div className="hidden md:flex items-center space-x-4">
            <a
              href="tel:+38641123456"
              className="flex items-center text-gray-700 hover:text-medical-600 transition-colors"
            >
              <Phone size={18} className="mr-2" />
              <span className="hidden lg:inline">+386 41 123 456</span>
            </a>
            <Button className="bg-medical-500 hover:bg-medical-600 text-white rounded-full btn-hover-effect">
              Rezerviraj posvet
            </Button>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden text-gray-700 hover:text-medical-600 focus:outline-none"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Navigation */}
      <div
        className={`md:hidden glass-card fixed inset-y-0 right-0 w-full max-w-xs transform transition-transform duration-300 ease-in-out ${
          isOpen ? "translate-x-0" : "translate-x-full"
        } p-6 space-y-6`}
      >
        <div className="flex justify-between items-center">
          <span className="text-xl font-bold text-gray-900">
            Dr. Danijel <span className="text-medical-600">Novak</span>
          </span>
          <button
            onClick={() => setIsOpen(false)}
            className="text-gray-700 hover:text-medical-600 focus:outline-none"
          >
            <X size={24} />
          </button>
        </div>

        <nav className="flex flex-col space-y-3">
          {navItems.map((item) => (
            <Link
              key={item.name}
              to={item.path}
              className={`px-4 py-3 rounded-xl text-base font-medium transition-colors duration-200 ${
                isActive(item.path)
                  ? "text-medical-600 bg-medical-50"
                  : "text-gray-700 hover:text-medical-600 hover:bg-gray-50"
              }`}
            >
              {item.name}
            </Link>
          ))}
        </nav>

        <div className="pt-4 space-y-4">
          <a
            href="tel:+38641123456"
            className="flex items-center text-gray-700 hover:text-medical-600 transition-colors"
          >
            <Phone size={18} className="mr-2" />
            <span>+386 41 123 456</span>
          </a>
          <Button className="w-full bg-medical-500 hover:bg-medical-600 text-white rounded-full">
            Rezerviraj posvet
          </Button>
        </div>
      </div>
    </header>
  );
};

export default Navbar;